/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 Select, NS/Tractie select-pendant overrides.
// Tractie source: select.
// Cluster 13 control-sizing tokens drive height/padding,
// cluster 6 spacing tokens drive chevron-area inset.
//
// Token references (Tractie -> NS):
//   - chevron color: --tractie-color-content-emphasis -> --ns-color-fg-emphasis (cluster 2)
//   - chevron inset-inline-end: --tractie-spacing-spacer-3 (12px) -> --ns-space-3 (cluster 6)
//   - chevron inset-block-start: 12px -> --ns-space-3 (cluster 6)
//   - right-padding (chevron-area): --tractie-spacing-spacer-10 (40px) -> --ns-space-10 (cluster 6)
//   - chevron 24x24: --ns-control-icon-size-l (cluster 13)
//   - select root height/padding inherits from input basis (--ns-control-height-m,
//     --ns-control-padding-y-m, --ns-control-padding-x-m).
//
// MUI v4 Select rendert via OutlinedInput-host; root/select/icon/disabled/iconOpen
// classes hier overriden, container-styling (border, focus, hover) komt van
// MuiOutlinedInput-overrides.

export const MuiSelectLightOverrides = {
  root: {
    // Fixed height + border-box: Tractie inner [slot="input"] rendert 39.6px
    // totaal (40px control-height met border-box border-compensation). MUI's
    // standaard `min-height` patroon gaf 41.6px (40 + 0.8×2 border) — content-
    // box-like overflow. Forced height + border-box matched Tractie 39.6.
    height: 'var(--ns-control-height-m)',
    minHeight: 'var(--ns-control-height-m)',
    boxSizing: 'border-box' as const,
  },

  select: {
    minHeight: 'var(--ns-control-height-m)',
    boxSizing: 'border-box' as const,
    // Padding-y: Tractie rendert 7px (niet de 8px van --ns-control-padding-y-m
    // token) zodat content-area = 40 − 14 − 2×0.8 ≈ 24.4px ≈ line-height 24px
    // exact past. Hardcoded calc voor pixel-pariteit met Tractie spec.
    paddingTop: 'calc(var(--ns-control-padding-y-m) - 1px)',
    paddingBottom: 'calc(var(--ns-control-padding-y-m) - 1px)',
    paddingLeft: 'var(--ns-control-padding-x-m)',
    // Padding-right: Tractie rendert 39px (--ns-space-10 minus 1px border
    // compensation). Exacte chevron-area match.
    paddingRight: 'calc(var(--ns-space-10) - 1px)',
    color: 'var(--ns-color-fg-default)',
    '&:focus': {
      backgroundColor: 'transparent',
      borderRadius: 0,
    },
    // Disabled opacity 0.7 — Tractie Select-spec (geverifieerd via
    // getComputedStyle op tractie-select [slot=input] met disabled attribuut).
    // Wijkt af van de 0.4-0.5 die andere Tractie-componenten gebruiken
    // (Tag, Switch) — per regel #10 per-component Tractie-leading.
    '&$disabled': {
      cursor: 'default' as const,
      pointerEvents: 'none' as const,
      opacity: 0.7,
    },
  },

  // Tractie chevron-area is 39px (40px token − 1px border-compensation).
  filled: {
    '&&': {
      paddingRight: 'calc(var(--ns-space-10) - 1px)',
    },
  },

  outlined: {
    borderRadius: 'var(--ns-control-radius)',
    '&&': {
      paddingRight: 'calc(var(--ns-space-10) - 1px)',
    },
  },

  selectMenu: {
    height: 'auto' as const,
    minHeight: 'var(--ns-control-height-m)',
    lineHeight: 1.5,
  },

  disabled: {},

  icon: {
    position: 'absolute' as const,
    right: 'var(--ns-space-3)',
    top: 'calc(50% - (var(--ns-control-icon-size-l) / 2))',
    width: 'var(--ns-control-icon-size-l)',
    height: 'var(--ns-control-icon-size-l)',
    pointerEvents: 'none' as const,
    color: 'var(--ns-color-fg-emphasis)',
    '&$disabled': {
      color: 'var(--ns-color-fg-subtle)',
    },
  },

  iconOpen: {
    transform: 'rotate(180deg)',
  },

  iconFilled: {
    right: 'var(--ns-space-3)',
  },

  iconOutlined: {
    right: 'var(--ns-space-3)',
  },
} as const;

// Dark spreads Light: chevron color/contrast already token-driven (cluster 2
// fg-emphasis flips dark via tokens.css), no mode-specific divergences.
export const MuiSelectDarkOverrides = {
  ...MuiSelectLightOverrides,
} as const;
